<script setup>
import { onMounted, watch, computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { initFlowbite } from 'flowbite'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useUserStore } from './stores/UserStore'
import { useListingsStore } from '@/stores/ListingsStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useHomeStore } from '@/stores/HomeStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut'
import { usePusher } from '@/hooks/usePusher'
import { useCello } from '@/hooks/useCello'
import { useStrapiStore } from '@/stores/StrapiStore'
import MainLayout from '@/components/MainLayout/index.vue'
import GlobalVideoWidget from '@/components/GlobalVideoWidget.vue'
import NewVersionToast from '@/components/NewVersionToast.vue'
import ListingsExportToast from '@/components/ListingsExportToast.vue'
import MaintenancePage from '@/views/MaintenancePage.vue'
import { setErrorHandlerUser } from '@/services/errorHandler'
import ToastNotification from '@/components/ToastNotification.vue'
import UpgradeFeatureModal from '@/components/UpgradePlans/UpgradeFeatureModal.vue'
import UpgradeWindow from '@/components/UpgradePlans/UpgradeWindow.vue'
import UpgradePlanSuccessModal from '@/components/UpgradePlans/UpgradePlanSuccessModal.vue'
import { POSTHOG_EVENTS } from '@/constants/posthog'
import { safeCapture } from '@/helpers/posthog'
import ZendeskButton from '@/components/ZendeskButton.vue'
import BuyScansDialog from '@/views/Settings/components/Plans/BuyScansDialog.vue'

const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE

const route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const globalStore = useGlobalStore()
const listingsStore = useListingsStore()
const configStore = useConfigStore()
const billingStore = useBillingStore()
const connectionStore = useConnectionStore()
const homeStore = useHomeStore()
const strapiStore = useStrapiStore()
const isAuthLayout = computed(() => !route.meta?.requireAuth)
const user = computed(() => userStore.user)

useKeyboardShortcut(router)
usePusher(userStore)
const { updateCelloPosition } = useCello()

globalStore.fetchBuildTxt()
strapiStore.fetchSystemStatusNotificationBar()

setInterval(() => {
  globalStore.fetchBuildTxt()
}, 300000)

onMounted(async () => {
  globalStore.windowWidth = window.innerWidth
  globalStore.windowHeight = window.innerHeight
  window.addEventListener('resize', () => {
    globalStore.windowWidth = window.innerWidth
    globalStore.windowHeight = window.innerHeight

    if (window.innerWidth < 1280) {
      globalStore.sidebar = false
    }

    updateZendeskPosition()
  })

  // initialize components based on data attribute selectors
  initFlowbite()

  const params = new URLSearchParams(window.location.search)
  const authAs = params.get('authAs')
  const sessionOverrideAuthToken = sessionStorage.getItem('cdp_auth_override_token')

  if (authAs || sessionOverrideAuthToken) {
    await userStore.checkSession(authAs || sessionOverrideAuthToken)
    console.log('logged in as', userStore.user?.email)

    if (route.query?.authAs) {
      router.replace('/inventory')
    } else if (route.query?.backTo) {
      router.replace(route.query.backTo)
    }
    
  } else {
    userStore.checkSession()
  }

  if (navigator.userAgent.search('Mac') !== -1) {
    globalStore.userAgent = 'cmd'
  } else {
    globalStore.userAgent = 'ctrl'
  }
  globalStore.sidebar =  JSON.parse(localStorage.getItem('sidebar') || false)
  localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
})

watch(user, () => {
  userStore.user && !configStore.ignoreConfigUpdate && configStore.fetchConfigs({ withoutLoader: true })
  userStore.user && setErrorHandlerUser(userStore.user)

  configStore.ignoreConfigUpdate = false

  if (!userStore.flags[`ebay_listings_corrected`]) {
    listingsStore.correctListings('ebay')
  }
})

watch(() => connectionStore.addedChannels.length, (newLen, oldLen) => {
  if (newLen === 1 && oldLen === 0) {
    homeStore.updateCaptureAction('added-connection')

    safeCapture(POSTHOG_EVENTS.CONNECTION_ADDED, {
      userId: userStore.user.id,
      connection_type: connectionStore.addedChannels[0].name,
      timestamp: new Date().toISOString(),
    })
  }
})

// Reactive position for the custom Zendesk trigger button
const zendeskButtonBottom = ref('18px')
const zendeskButtonRight = ref('26px')
const showZendeskButton = ref(false)

const safeZE = (...args) => {
  try {
    if (typeof zE !== 'undefined') zE(...args)
  } catch (e) {}
}

const setZendeskPosition = (bottom, right) => {
  // Update custom button position
  zendeskButtonBottom.value = bottom
  zendeskButtonRight.value = right

  // Position the open chat panel just above the custom button.
  // Button is 48px tall + 8px gap = 56px offset.
  const panelBottom = `${parseInt(bottom) + 56}px`

  let style = document.getElementById('ze-position-override')
  if (!style) {
    style = document.createElement('style')
    style.id = 'ze-position-override'
    document.head.appendChild(style)
  }
  style.textContent = `
    #launcher,
    iframe[title*="Button to launch"] { display: none !important; }
    iframe[title*="Messaging window"],
    iframe[title*="messaging window"] { bottom: ${panelBottom} !important; right: ${right} !important; }
  `

  updateCelloPosition(bottom, right)
}

const updateZendeskPosition = () => {
  const hiddenRoutes = ['MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted', 'SignIn', 'SignUp', 'SignUpEmail', 'ResetPassword', 'SetPassword', 'EmailSent', 'LinkExpired']
  const isMobile = window.innerWidth < 768

  if (hiddenRoutes.includes(route.name) || (isMobile && globalStore.sidebar)) {
    safeZE('messenger', 'hide')
    showZendeskButton.value = false
  } else if (['/batches', '/inventory'].includes(route.path)) {
    safeZE('messenger', 'show')
    showZendeskButton.value = true
    setZendeskPosition(isMobile ? '70px' : '55px', globalStore.videoWidgetOpen ? '380px' : (isMobile ? '18px' : '75px'))
  } else if (route.path.includes('history')) {
    safeZE('messenger', 'show')
    showZendeskButton.value = true
    setZendeskPosition(isMobile ? '65px' : '50px', globalStore.videoWidgetOpen ? '380px' : '18px')
  } else {
    safeZE('messenger', 'show')
    showZendeskButton.value = true
    setZendeskPosition('18px', globalStore.videoWidgetOpen ? '380px' : '18px')
  }
}

watch(route, updateZendeskPosition, { immediate: true })
watch(() => globalStore.videoWidgetOpen, updateZendeskPosition)
watch(() => globalStore.sidebar, updateZendeskPosition)

// Global error handler
window.onerror = function (message, source, lineno, colno, error) {
  if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected:', {
      message,
      source,
      lineno,
      colno,
      error
    })
  }

  if (error instanceof RangeError) {
    console.error('Global RangeError Caught:', error)
  }

  // Return false to prevent the default handling of the error
  return false
};



// Event listener for global errors
window.addEventListener('error', function (event) {
  if (event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected:', {
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  }
});

// Event listener for unhandled promise rejections
window.addEventListener('unhandledrejection', function (event) {
  if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected (unhandled rejection):', event.reason);
  }
});
</script>

<template>
  <!-- <RouterView v-if="isAuthLayout || configStore.config?.subscription?.plan_id" /> -->
  <MaintenancePage v-if="isMaintenanceMode === 'true' || globalStore.maintenanceMode" />
  <template v-else>
    <NewVersionToast />
    <ListingsExportToast />
    <ToastNotification />
    <ZendeskButton
      v-if="showZendeskButton"
      :bottom="zendeskButtonBottom"
      :right="zendeskButtonRight"
    />

    <RouterView v-if="isAuthLayout || ['MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted'].includes(route.name)" />
    <template v-else>
      <MainLayout />
      <GlobalVideoWidget />

      <UpgradeFeatureModal />
      <UpgradeWindow v-if="!configStore.isComplimentary && billingStore.upgradeWindow" @close="billingStore.upgradeWindow = false" />
      <UpgradePlanSuccessModal v-if="billingStore.upgradePlanSuccessModal" @close="billingStore.upgradePlanSuccessModal = false" />
      <BuyScansDialog />
    </template>
  </template>
</template>